import React from "react";
import { Stack, Text } from "@primer/react";
import { Table, DataTable } from "@primer/react/experimental";
import { FilterIcon } from '@primer/octicons-react';
import { formatIsoDate } from "../../helpers/dates";
import { renderTruncatedText } from "../../helpers/strings";

export interface TableColumnConfig {
  id: string;
  header: string | (() => React.ReactNode);
  field: string;
  rowHeader?: boolean
  maxWidth?: React.CSSProperties['maxWidth']
  sortBy?: boolean | "alphanumeric" | "datetime" | "basic" | ((a: any, b: any) => number);
  renderCell?: (row: any) => React.ReactNode;
}

export const TableComponent = ({ data, config, pageIndex, toggleSort }: {
  data: any;
  config: any;
  pageIndex: number;
  toggleSort?: (columnId: string | number, direction: 'ASC' | 'DESC') => void
}) => {
  const columns = config.map((col: any) => ({
    header: col.header,
    field: col.field,
    rowHeader: col.rowHeader,
    maxWidth: col.maxWidth,
    ...(col.sortBy && { sortBy: col.sortBy }),
    ...(col.renderCell && { renderCell: col.renderCell }),
  }));

  return (
    <>
      <Table.Title as="h2" id="users-table">
        Table ({data.length}) Page ({pageIndex})
      </Table.Title>
      <DataTable
        aria-labelledby="users-table"
        data={data}
        columns={columns}
        onToggleSort={toggleSort}
      />
    </>
  );
};

export function createDataTableColumns(
  columnConfigs: any[],
  handleSort: (field: any) => void,
  getSortIcon: (field: any) => any,
  getSortText: (field: any, title: string) => string
) {
  return columnConfigs.map(config => ({
    id: config.id as string,
    header: config.sortable ? (() =>
      <button onClick={() => handleSort(config.id as string)}>
        <Stack align="center" direction="horizontal" gap="none">
          {getSortIcon(config.id as string)}
          <Text className="ml-[4.5px]">{getSortText(config.id as string, config.title)}</Text>
        </Stack>
      </button>
    ) : (() => config.title),
    renderCell: config.render
  }));
}

export function createDataTableFilterColumns(
  columnConfigs: any[],
  handleFilter: (field: any) => void,
  isActiveFilter: (field: any) => boolean,
) {
  return columnConfigs.map(config => ({
    id: config.id as string,
    header: config.filtered ? (() =>
      <button onClick={() => handleFilter(config.id as string)}>
        <Stack align="center" direction="horizontal" gap="condensed">
          <Text className="ml-[4.5px]">{config.title}</Text>
          <FilterIcon className={`${isActiveFilter(config.id) ? "text-[#0969DA]" : "text-[#59636E]"}`} />
        </Stack>
      </button>
    ) : (() => config.title),
    renderCell: config.render
  }));
}


export const metricsTableConfig = (count: number) => {
  return Array.from({ length: count }, (_, index) => ({
    id: index.toString(),
    header: "",
    field: index.toString(),
  }));
}

export interface TableColumn<T> {
  id: string;
  title: string;
  sortable: boolean;
  filtered?: boolean;
  render: (row: T) => any;
}

export const createRenderColumn = <T extends Record<string, any>>(
  id: string,
  title: string,
  render: (row: T) => React.ReactNode,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render
});

export const createSimpleColumn = <T extends Record<string, any>>(
  id: string,
  title: string,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render: (row: T) => {
    const value = row[id];
    if (typeof value === 'boolean') {
      return <span>{value ? 'Да' : 'Нет'}</span>;
    }
    if (value === "buyer") {
      return <span>Покупатель</span>;
    }
    if (value === "seller") {
      return <span>Продавец</span>;
    }
    if (value === "buyer") {
      return <span>Покупатель</span>;
    }
    return renderTruncatedText(value, 48);
  }
});

export const createSimpleColumnWithFallback = <T extends Record<string, any>>(
  ids: string[],
  title: string,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id: ids[0],
  title,
  sortable,
  filtered,
  render: (row: T) => {
    const value = ids.find(id => row[id] != null);
    return renderTruncatedText(value ? row[value] : null, 48);
  }
});

export const createPercentColumn = <T extends Record<string, any>>(
  id: string,
  title: string,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render: (row: T) => `${row[id]}%`
});

export const createDateColumn = <T extends Record<string, any>>(
  id: string,
  title: string,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render: (row: T) => formatIsoDate(row[id], 'full')
});

export const createTimeColumn = <T extends Record<string, any>>(
  id: string,
  title: string,
  sortable: boolean = false,
  withSeconds: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render: (row: T) => formatIsoDate(row[id], withSeconds ? 'timeWithSeconds' : 'time')
});

export const createMonthColumn = <T extends Record<string, any>>(
  id: string,
  title: string,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render: (row: T) => formatIsoDate(row[id], 'month')
});

export const createMappedColumn = <T extends Record<string, any>, V = any>(
  id: string,
  title: string,
  mapFunction: (value: V) => string,
  sortable: boolean = false,
  filtered: boolean = false
): TableColumn<T> => ({
  id,
  title,
  sortable,
  filtered,
  render: (row: T) => {
    const value = row[id];
    if (value === null || value === undefined) {
      return <span>-</span>;
    }
    const text = mapFunction(value as V);
    return renderTruncatedText(text, 48)
  }
});